import { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { CloseIcon, ChevronLeft, ChevronRight, DownloadIcon } from "./ui";

/**
 * Full-screen image viewer for chat photos. `images` is every image URL in the
 * open thread (oldest → newest) so ← / → step through them; `index` is the one
 * that was clicked. Closes on Escape, backdrop click or the ✕.
 */
export default function Lightbox({ images = [], index = 0, onClose, onIndex }) {
  const count = images.length;
  const url = images[index];

  const step = useCallback(
    (delta) => {
      if (count < 2) return;
      onIndex((index + delta + count) % count);
    },
    [count, index, onIndex]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, step]);

  if (!url) return null;

  const Nav = ({ dir, children }) => (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        step(dir);
      }}
      aria-label={dir > 0 ? "Next image" : "Previous image"}
      className={`absolute top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/25 ${
        dir > 0 ? "right-3 sm:right-6" : "left-3 sm:left-6"
      }`}
    >
      {children}
    </button>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute right-3 top-3 z-10 flex items-center gap-2 sm:right-5 sm:top-5">
        <a
          href={url}
          download
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          aria-label="Download"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/25"
        >
          <DownloadIcon className="h-5 w-5" />
        </a>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/25"
        >
          <CloseIcon className="h-5 w-5" />
        </button>
      </div>

      {count > 1 && (
        <>
          <Nav dir={-1}><ChevronLeft className="h-5 w-5" /></Nav>
          <Nav dir={1}><ChevronRight className="h-5 w-5" /></Nav>
          <span className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
            {index + 1} / {count}
          </span>
        </>
      )}

      <img
        src={url}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="chat-pop max-h-[86vh] max-w-full rounded-2xl object-contain shadow-2xl"
      />
    </div>,
    document.body
  );
}
